import { HeatmapWidget as GoHeatmapWidget } from '@go-widgets/well-logs-widget/dist/HeatmapWidget/HeatmapWidget';
import { FullArrowLight as FullIcon, ReduceLight as MinimizeIcon } from '@repo/go-icons';
import { Button, ConfigContext, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useService } from 'src/packages/di';

import { NoWellChosen } from '../no-well-chosen';
import { WidgetHeader } from '../widget-header/WidgetHeader';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { HeatmapWidgetEntity } from './HeatmapWidget.entity';
import styles from './HeatmapWidget.module.scss';
import { HeatmapWidgetStore } from './HeatmapWidget.store';

type WrapperProps = {
  entity: HeatmapWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: HeatmapWidgetStore;
  entity: HeatmapWidgetEntity;
  options: TWidgetRendererOptions;
};

export const HeatmapWidgetWrapper = observer(function HeatmapWidgetWrapper({ entity, options }: WrapperProps) {
  const [store, setStore] = useState<HeatmapWidgetStore | null>(null);

  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const _store = storeService.getStore<HeatmapWidgetEntity, HeatmapWidgetStore>(entity);

    if (_store) {
      setStore(_store);
    } else {
      const newStore = new HeatmapWidgetStore(entity, options.groupSelectStore, options.wellListEntity);

      storeService.setStore<HeatmapWidgetEntity, HeatmapWidgetStore>(entity, newStore);

      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store, options.wellListEntity]);

  if (!store) return null;

  return <HeatmapWidget store={store} entity={entity} options={options} />;
});

export const HeatmapWidget = observer(function HeatmapWidget({ entity, options, store }: Props) {
  const globalConfig = useContext(ConfigContext);
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const { effect, setGroupForWellLogs } = store;
  const { wellId, isFullscreen, state, setFullscreen, wellboreName } = entity;

  useEffect(effect, [effect]);

  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName = wells?.find((item) => item.id === wellId)?.data.name ?? wellId;

  return (
    <div className={styles.heatmapWidgetWrapper} ref={(ref) => setWrapperElement(ref)}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={setGroupForWellLogs}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        wellName={wellName}
        wellboreName={wellboreName}
        onWidgetDelete={() => options.onWidgetDelete(entity)}
        progress={store.progress}
        showCloseButton={!isFullscreen}
        draggable={!isFullscreen}
        renderAdditionalButtons={() => {
          return (
            <>
              {!isFullscreen && (
                <Tooltip title={t('common:Widget.fullscreen')} delay={400}>
                  <Button
                    onClick={() => setFullscreen(true)}
                    variant="flat"
                    icon={<FullIcon className={styles.icon} />}
                    className={styles.button}
                  />
                </Tooltip>
              )}
              {isFullscreen && (
                <Tooltip title={t('common:Widget.collapse')} delay={400}>
                  <Button
                    onClick={() => store.exitFullScreen(globalConfig)}
                    variant="flat"
                    icon={<MinimizeIcon className={styles.icon} />}
                    disabled={store.isExitFullscreenFetching}
                    className={styles.button}
                  />
                </Tooltip>
              )}
            </>
          );
        }}
      />
      {wrapperElement &&
        (state && wellId ? (
          <div className={styles.wrapper}>
            <GoHeatmapWidget widgetState={state} />
          </div>
        ) : (
          <NoWellChosen />
        ))}
    </div>
  );
});
